import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { Dietitian } from 'src/app/data/dietitian/domain/dietitian';
import { Patient } from 'src/app/data/patient/domain/patient';
import { ConfirmationModalComponent } from 'src/app/ui/components/confirmation-modal/confirmation-modal.component';

import { QuestionsService, SortListType } from '../../questions.service';
import { AddEncouragementModalComponent } from '../patients-list/components/add-encouragement-modal/add-encouragement-modal.component';
import { DiariesListFacade } from './diaries-list.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-diaries-list',
  templateUrl: './diaries-list.component.html',
  styleUrls: ['./diaries-list.component.scss'],
  providers: [DiariesListFacade],
})
export class DiariesListComponent implements OnDestroy {
  private dietitianStateSub?: Subscription;
  private selectedSortListTypeSub?: Subscription;
  private selectedGroupSub?: Subscription;
  private patientGroupEventsSub?: Subscription;
  private patientEventsSub?: Subscription;
  private dietitian: Dietitian | undefined;
  patientsList: Patient[] = [];
  isLoading = true;
  error?: string;
  selectedPatient: Patient | undefined;
  bsModalRef?: BsModalRef;
  isUpdating = false;

  constructor(
    private facade: DiariesListFacade,
    private modalService: BsModalService,
    private router: Router,
    public questionsService: QuestionsService,
  ) {
    this.dietitianStateSub = this.facade.dieititianState$.subscribe((state) => {
      const reload = !state.entity?.dietitianId.equals(
        this.dietitian?.dietitianId,
      );
      this.dietitian = state.entity;
      if (reload) this.loadPatients();
    });

    this.patientGroupEventsSub = this.facade.patientGroupEvents$.subscribe(
      (event) => {
        if (
          event.entity.patientGroupId.equals(
            this.questionsService.selectedGroup?.patientGroupId,
          )
        ) {
          this.loadPatients();
        }
      },
    );

    this.patientEventsSub = this.facade.patientEvents$.subscribe(() =>
      this.loadPatients(),
    );

    this.selectedSortListTypeSub =
      this.questionsService.selectedSortListTypeEmitter.subscribe(() => {
        this.loadPatients();
      });

    this.selectedGroupSub =
      this.questionsService.selectedGroupEmitter.subscribe(() => {
        this.loadPatients();
      });
  }

  async loadPatients() {
    this.isLoading = true;
    this.error = undefined;

    if (!this.dietitian) {
      this.patientsList = [];
      this.isLoading = false;
      this.selectPatient();
      return;
    }

    try {
      if (
        this.questionsService?.selectedSortListType == SortListType.Group &&
        this.questionsService?.selectedGroup
      ) {
        this.patientsList =
          await this.facade.getDietitianPatientsWithUnreadDiaries(
            this.dietitian.id.toString(),
            this.questionsService?.selectedGroup.id.toString(),
            false,
          );
      } else if (
        this.questionsService?.selectedSortListType == SortListType.Archived
      ) {
        this.patientsList =
          await this.facade.getDietitianPatientsWithUnreadDiaries(
            this.dietitian.id.toString(),
            null,
            true,
          );
      } else {
        this.patientsList =
          await this.facade.getDietitianPatientsWithUnreadDiaries(
            this.dietitian.id.toString(),
            null,
            false,
          );
      }
      if (this.selectedPatient) {
        this.selectPatient(
          this.patientsList.find((p) =>
            p.patientId.equals(this.selectedPatient?.patientId),
          ),
        );
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
    }
    this.isLoading = false;
  }

  markPatientsDiariesAsRead() {
    if (!this.dietitian) return;

    const options: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: 'Marquer tous les journaux comme lus ?',
        subtitle: 'En validant, tous les journaux seront marqués comme lus.',
      },
    };
    this.bsModalRef = this.modalService.show(
      ConfirmationModalComponent,
      options,
    );
    if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
      this.bsModalRef.content.validate.subscribe(async () => {
        if (!this.dietitian) return;

        this.isUpdating = true;

        try {
          if (
            this.questionsService?.selectedSortListType == SortListType.Group &&
            this.questionsService?.selectedGroup
          ) {
            await this.facade.markDietitianPatientsDiariesAsRead(
              this.dietitian?.id.toString(),
              this.questionsService?.selectedGroup?.id.toString(),
              false,
            );
          } else if (
            this.questionsService?.selectedSortListType == SortListType.Archived
          ) {
            await this.facade.markDietitianPatientsDiariesAsRead(
              this.dietitian?.id.toString(),
              null,
              true,
            );
          } else {
            await this.facade.markDietitianPatientsDiariesAsRead(
              this.dietitian?.id.toString(),
              null,
              false,
            );
          }
          this.isUpdating = false;
        } catch (error) {
          this.isUpdating = false;
          throw error;
        }
      });
    }
  }

  async markPatientDiariesAsRead(patientId: string) {
    this.isUpdating = true;
    try {
      await this.facade.markPatientDiariesAsRead(patientId);
      this.isUpdating = false;
    } catch (error) {
      this.isUpdating = false;
      throw error;
    }
  }

  navigateToPatientRecord(id: string): void {
    this.router.navigate(['/patient', id]);
  }

  selectPatient(patient?: Patient) {
    this.selectedPatient = patient;
  }

  onSendEncouragement() {
    const initialState: ModalOptions = {
      class: 'modal-lg modal-dialog-centered',
      initialState: {
        target: this.selectedPatient,
        dietitian: this.dietitian,
      },
    };
    this.bsModalRef = this.modalService.show(
      AddEncouragementModalComponent,
      initialState,
    );
  }

  ngOnDestroy(): void {
    this.dietitianStateSub?.unsubscribe();
    this.patientGroupEventsSub?.unsubscribe();
    this.patientEventsSub?.unsubscribe();
    this.selectedSortListTypeSub?.unsubscribe();
    this.selectedGroupSub?.unsubscribe();
  }
}
